import type { ReactElement } from 'react';
import { Button } from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  replaceSearchOperatorToken,
  searchOperatorFeedback,
  type SearchOperatorSuggestion,
} from '@/lib/search-operators';

export interface OperatorHelpProps {
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly compact?: boolean;
}

export function OperatorHelp({
  query,
  onQueryChange,
  compact = false,
}: OperatorHelpProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const feedback = searchOperatorFeedback(query);
  const hasActive = feedback.active.length > 0;
  const hasWarnings = feedback.warnings.length > 0;
  const hasSuggestions = feedback.suggestions.length > 0;

  function suggestionDescription(suggestion: SearchOperatorSuggestion): string {
    if (suggestion.id.startsWith('has-')) {
      return t(`search.operators.description.has.${suggestion.id.slice('has-'.length)}`);
    }
    if (suggestion.id.startsWith('due-')) {
      return t('search.operators.description.due');
    }
    if (suggestion.id.startsWith('created-')) {
      return t('search.operators.description.created');
    }
    if (suggestion.id === 'edited-365') {
      return t('search.operators.description.edited-year');
    }
    if (suggestion.id.startsWith('edited-')) {
      return t('search.operators.description.edited');
    }
    if (suggestion.id.startsWith('is-')) {
      return t('search.operators.description.status');
    }
    if (suggestion.id.startsWith('sort-')) {
      return t('search.operators.description.sort');
    }
    const key = `search.operators.description.${suggestion.id}`;
    const translated = t(key);
    return translated === key ? suggestion.description : translated;
  }

  return (
    <div
      role="region"
      aria-label={t('search.operators.help')}
      className={cn('space-y-2 text-ink-muted', compact ? 'text-xs' : 'text-sm')}
    >
      {hasActive ? (
        <section aria-label={t('search.operators.active')} className="space-y-1">
          <p className="font-medium text-ink">{t('search.operators.activeTitle')}</p>
          <div className="flex flex-wrap gap-1.5">
            {feedback.active.map((summary) => (
              <span
                key={summary.id}
                className="rounded-card border border-border bg-surface px-2 py-0.5 font-mono text-xs text-ink"
              >
                {t(summary.messageKey, summary.params, summary.count)}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      {hasWarnings ? (
        <section
          aria-label={t('search.operators.guidance')}
          aria-live="polite"
          className="space-y-1"
        >
          <p className="font-medium text-ink">{t('search.operators.check')}</p>
          <ul className="space-y-1">
            {feedback.warnings.map((warning) => (
              <li key={warning.id}>{t(warning.messageKey, warning.params)}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {hasSuggestions ? (
        <section aria-label={t('search.operators.suggestions')} className="space-y-1.5">
          <p className="font-medium text-ink">{t('search.operators.suggestionsTitle')}</p>
          <div className="flex flex-wrap gap-1.5">
            {feedback.suggestions.map((suggestion) => {
              const description = suggestionDescription(suggestion);
              return (
                <Button
                  key={suggestion.id}
                  type="button"
                  size="sm"
                  variant="quiet"
                  aria-label={`${suggestion.token} ${description}`}
                  className="h-auto min-h-7 justify-start px-2 py-1 text-left"
                  onClick={() =>
                    onQueryChange(replaceSearchOperatorToken(query, suggestion))
                  }
                >
                  <span className="inline-flex flex-wrap gap-x-1.5 gap-y-0.5">
                    <span className="font-mono text-ink">{suggestion.token}</span>
                    <span>{description}</span>
                  </span>
                </Button>
              );
            })}
          </div>
        </section>
      ) : null}

      {!hasActive && !hasWarnings && !hasSuggestions ? (
        <p>{t('search.operators.fallback')}</p>
      ) : null}
    </div>
  );
}
